import {
  Grid,
  Fade,
  InputLabel,
  TextField,
  Typography,
  Button,
  LinearProgress,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import React, { useState } from 'react'
import { basicStyles } from '../styles/styles'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import EquinorLoginButton from '../components/EquinorLoginButton'
import { getEnvFlag } from '../env'

import loginImage1 from '../assets/customers/login_image_1.jpg'
import loginImage2 from '../assets/customers/login_image_2.jpg'
import loginImage3 from '../assets/customers/login_image_3.jpg'
import conwxLogo from '../assets/customers/conwx_logo.svg'

const isSsoEnabled = getEnvFlag('REACT_APP_ENABLE_AAD_LOGIN')
const isPasswordLoginDisabled = getEnvFlag('REACT_APP_DISABLE_PASSWORD_LOGIN')

const useStyles = makeStyles(theme => ({
  ...basicStyles(theme),
  // Image container to be able to use a `coverImage`.
  coverImageContainer: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  // Cover Image: used for the background image when we want to show a full page image
  coverImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  // container: used for the container of the form and logo in the right drawer, centered
  formContainer: {
    width: 260,
    margin: '65px 80px 65px 80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  //form: used for the form in the right drawer
  form: {
    width: '100%',
  },
  // Logo: used for the logo in the right drawer
  logo: {
    margin: '0px 0px 36px 0px',
    height: 97,
    width: 260,
    '& path': {
      fill: theme.palette.company.logoColor,
    },
  },
}))

const LoginPage = props => {
  const classes = useStyles(props)
  const {
    onSubmitUser,
    onSsoLogin,
    ssoLoading,
    ssoError,
    errorMessage,
    setErrorMessage,
    loading,
  } = props

  const possibleLoginImages = [loginImage1, loginImage2, loginImage3]
  const [randomIndex] = useState(
    Math.floor(Math.random() * possibleLoginImages.length),
  )

  const loginImage = possibleLoginImages[randomIndex]

  const [formState, setFormState] = useState({
    username: '',
    usernameError: '',
    password: '',
    passwordError: '',
  })
  const [showPassword, setShowPassword] = useState(false)

  const validateForm = () => {
    const { username, password } = formState
    const errors = {
      usernameError: !username ? 'Username is required' : '',
      passwordError: !password ? 'Password is required' : '',
    }
    setErrorMessage('')
    setFormState(prevFormState => ({ ...prevFormState, ...errors }))
    return Object.values(errors).every(error => !error)
  }

  const onSubmit = e => {
    e.preventDefault()

    if (validateForm()) {
      const client = {
        name: formState.username,
        password: formState.password,
      }

      onSubmitUser(client, true)
    }
  }

  const onChange = field => e => {
    setFormState({ ...formState, [field]: e.target.value })
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }

  return (
    <Grid container spacing={0} className={classes.fullPageContainer}>
      <Grid item xs className={classes.coverImageContainer}>
        <Fade in={true}>
          <img src={loginImage} className={classes.coverImage} alt="login" />
        </Fade>
      </Grid>
      <Grid item className={classes.drawer}>
        <div className={classes.formContainer}>
          <img src={conwxLogo} alt="conwx logo" className={classes.logo} />

          {isSsoEnabled && (
            <>
              <EquinorLoginButton
                onLogin={onSsoLogin}
                loading={ssoLoading}
                error={ssoError}
              />
              {!isPasswordLoginDisabled && (
                <Divider style={{ width: '100%', margin: '12px 0' }}>
                  <Typography variant="body2" color="textSecondary">
                    or
                  </Typography>
                </Divider>
              )}
            </>
          )}

          {!isPasswordLoginDisabled && (
            <form onSubmit={onSubmit} className={classes.form}>
              <div className={classes.inputContainer}>
                <InputLabel>Username</InputLabel>
                <TextField
                  className={classes.inputTextField}
                  value={formState.username}
                  variant="outlined"
                  margin="dense"
                  size="small"
                  disabled={loading}
                  error={Boolean(formState.usernameError)}
                  helperText={formState.usernameError}
                  placeholder="Enter username"
                  onChange={onChange('username')}
                />
              </div>
              <div className={classes.inputContainer}>
                <InputLabel>Password</InputLabel>
                <TextField
                  className={classes.inputTextField}
                  value={formState.password}
                  variant="outlined"
                  margin="dense"
                  size="small"
                  disabled={loading}
                  type={showPassword ? 'text' : 'password'}
                  error={Boolean(formState.passwordError)}
                  helperText={formState.passwordError}
                  placeholder="Enter password"
                  onChange={onChange('password')}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography variant="body1" className={classes.inputErrorText}>
                  {errorMessage}
                </Typography>
              </div>
              {loading ? (
                <LinearProgress />
              ) : (
                <div className={classes.loadingPlaceholder} />
              )}
              <Button
                variant={'contained'}
                color={'primary'}
                disableElevation
                className={classes.submitButton}
                type="submit"
                style={{ margin: '3px 0px' }}
              >
                Login
              </Button>
            </form>
          )}
        </div>
      </Grid>
    </Grid>
  )
}

export default LoginPage
